import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

export interface FormatPhoneNumberOptions {
  /**
   * When provided, the formatter will attempt to interpret national numbers using this country.
   * Falls back to the phone's own country code when parsing international numbers.
   */
  defaultCountry?: CountryCode;
  /**
   * If true, include the country calling code (E.164 / international display).
   * When false, display the best available national representation.
   * Defaults to false.
   */
  international?: boolean;
  /**
   * String to render when the value is empty or cannot be parsed.
   * Defaults to "—".
   */
  fallback?: string;
}

/**
 * Stored phone values that are not phone numbers: `email:<address>` for
 * email-booked customers and `walk-in:` for the business's walk-in customer.
 * They satisfy `@@unique([salonId, phoneNumber])` and must never be dialed,
 * texted, sent to Stripe, or displayed.
 */
export function isPlaceholderPhone(phone: string | null | undefined): boolean {
  if (typeof phone !== "string") return false;
  const trimmed = phone.trim();
  return trimmed.startsWith("email:") || trimmed.startsWith("walk-in:");
}

/**
 * The business's own walk-in customer (`walk-in:`), as opposed to an
 * email-booked person. It is a system row, not a contact: it is never
 * texted, emailed, or verified on any channel (`BF-DRIFT-054`).
 */
export function isWalkInPhone(phone: string | null | undefined): boolean {
  if (typeof phone !== "string") return false;
  return phone.trim().startsWith("walk-in:");
}

/**
 * Format a phone number for display using libphonenumber-js.
 * Handles both international and national formatting, with graceful fallbacks for invalid inputs.
 */
export function formatPhoneNumberForDisplay(
  value: string | null | undefined,
  options: FormatPhoneNumberOptions = {},
): string {
  const { defaultCountry, international = false, fallback = "—" } = options;

  if (!value) {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  // Placeholder phones ("email:user@example.com", the walk-in "walk-in:")
  if (isPlaceholderPhone(trimmed)) {
    return fallback;
  }

  const phone = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (phone) {
    try {
      if (international) {
        return phone.formatInternational();
      }

      if (phone.isValid()) {
        return phone.formatNational();
      }

      // US-first display: when the calling code is +1, prefer the national format
      // even if the number isn't strictly valid.
      if (phone.countryCallingCode === "1") {
        try {
          return phone.formatNational();
        } catch (_err) {}
      }

      return phone.formatInternational();
    } catch (_err) {}
  }

  const digitsOnly = trimmed.replace(/\D/g, "");
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(
      3,
      6,
    )}-${digitsOnly.slice(6)}`;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return `(${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(
      4,
      7,
    )}-${digitsOnly.slice(7)}`;
  }

  return trimmed;
}

/**
 * Attempt to normalise a phone number into E.164 format (e.g. +16505551234).
 * Returns null when the value cannot be parsed into a valid phone number.
 */
export function toE164(
  value: string | null | undefined,
  defaultCountry?: CountryCode,
): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const phone = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (phone && phone.isValid()) {
    return phone.number;
  }

  // If parsing failed and the value looks like it already has a country code
  // but is missing the +, try prepending it (e.g. "447484294028" → "+447484294028")
  if (!trimmed.startsWith("+")) {
    const withPlus = parsePhoneNumberFromString("+" + trimmed);
    if (withPlus && withPlus.isValid()) {
      return withPlus.number;
    }
  }

  return null;
}
